// Where the catalog comes from.
//
// Two implementations behind one shape: the real backend, and the design's
// fixture catalog. The UI never branches on which — it just renders what it's
// given and shows a banner naming the mode.
//
// The fallback is deliberately LOUD. ADR-0006 says vector features must fail
// with FAILED_PRECONDITION rather than silently degrade, for exactly the
// reason that applies here: a quiet fixture fallback is indistinguishable from
// "the recommender is bad", and would waste a debugging session. So the mode
// is probed once, surfaced in the header, and never guessed at again.

import { Code, ConnectError } from "@connectrpc/connect";

import { catalogClient } from "../lib/connect";
import { FIXTURE_CATALOG } from "./fixtures";
import type { SourceMode, Work } from "./types";

export interface CatalogLoad {
  mode: SourceMode;
  works: Work[];
  /** Why we fell back, when we did. Shown verbatim in the banner. */
  reason?: string;
}

/**
 * Ask the backend for a single work. Anything other than a real answer means
 * the catalog isn't servable yet and we use fixtures.
 *
 * Unimplemented is the expected response until backend milestone M3, so it is
 * reported as a milestone message rather than an error. Everything else — a
 * dead backend, a 502 from the ingress — is reported as a genuine fault,
 * because those are worth acting on.
 */
export async function loadCatalog(signal?: AbortSignal): Promise<CatalogLoad> {
  try {
    const res = await catalogClient.listWorks({ pageSize: 200 }, { signal });
    if (!res.works.length) {
      return {
        mode: "fixture",
        works: FIXTURE_CATALOG,
        reason: "the catalog is empty — the crawler hasn't ingested anything yet",
      };
    }
    return { mode: "live", works: res.works.map(fromProto) };
  } catch (err) {
    if (signal?.aborted) throw err;
    return { mode: "fixture", works: FIXTURE_CATALOG, reason: describe(err) };
  }
}

function describe(err: unknown): string {
  if (err instanceof ConnectError) {
    if (err.code === Code.Unimplemented) {
      return "the backend hasn't implemented the catalog API yet (milestone M3)";
    }
    if (err.code === Code.FailedPrecondition) return err.rawMessage;
    return `the backend returned ${Code[err.code]}: ${err.rawMessage}`;
  }
  return "the backend is unreachable";
}

/**
 * Flatten an amrec.v1.Work onto the UI's view model.
 *
 * Two fields the proto doesn't carry yet, and how they're derived:
 *
 * - `moods` — the design treats mood as an axis independent of genre, and
 *   weights them separately (the "Vibe match" preset puts 0.46 on mood and
 *   0.26 on genre). In the proto these arrive as tags whose category is
 *   "mood", which is why they're split out here rather than merged.
 * - `len` — bucketed from episode/chapter counts, because "short/medium/long"
 *   is a UI affordance, not a catalog fact, and the thresholds differ by kind.
 */
function fromProto(w: import("../gen/amrec/v1/catalog_pb").Work): Work {
  const tags = w.tags ?? [];
  const named = (cat: string) =>
    tags.filter((t) => t.tag?.category === cat).map((t) => t.tag!.name);

  const kind = w.kind === 1 ? "anime" : "manga";
  const episodes = w.episodeCount || 0;
  const chapters = w.chapterCount || 0;
  const units = kind === "anime" ? episodes : chapters;
  const len = bucketLength(kind, units);

  // Prefer the highest-count source rather than averaging: MAL's and
  // AniList's score distributions are not comparable, and blending them
  // produces a number that means nothing on either scale.
  const best = [...(w.stats ?? [])].sort((a, b) => b.scoreCount - a.scoreCount)[0];

  return {
    id: w.id,
    kind,
    title: w.displayTitle,
    year: w.startDate?.year ?? 0,
    by: w.organizations?.find((o) => o.isMain)?.name ?? w.organizations?.[0]?.name ?? "Unknown",
    genres: named("genre").length ? named("genre") : tags.slice(0, 4).map((t) => t.tag!.name),
    moods: named("mood"),
    score: best ? best.score / 10 : 0,
    pop: best?.popularityRank ? Math.max(0, 100 - Math.min(100, best.popularityRank / 100)) : 0,
    len,
    status: w.status === 2 ? "airing" : w.status === 5 ? "hiatus" : "finished",
    count: units ? `${units} ${kind === "anime" ? "eps" : "ch"}` : "—",
    coverPath: w.cover?.proxyPath,
  };
}

function bucketLength(kind: "anime" | "manga", units: number): Work["len"] {
  if (kind === "anime") return units <= 13 ? "short" : units <= 50 ? "medium" : "long";
  return units <= 40 ? "short" : units <= 120 ? "medium" : "long";
}
